import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Typography,
  Button,
  Descriptions,
  Row,
  Col,
  Space,
  Spin,
  Tabs,
  Tag,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Package, PackageStatus, PackageType, DeliveryService } from '@/types/residents/package';
import { getPackageById, updatePackage, deletePackage } from '@/api/residents';
import { useAuth } from '@/contexts/AuthContext';
import ActivityLogList from '@/components/common/ActivityLogList';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import PackageModal from '../../components/features/residents/PackageModal';
import { ResidentTab, EmployeeTab } from '@/components/tab';

const { Title, Text } = Typography;


const PackageDetailsPage: React.FC = () => {
  const { t } = useTranslation(['packages', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  
  const state = location.state as { package: Package } | null;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Query - CRITICAL: Only fetch if no state data is available
  const { data: packageData, isLoading } = useQuery({
    queryKey: ['package', id],
    queryFn: () => getPackageById(id!, firestoreUser ?? undefined),
    enabled: !!id && !state?.package && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  // Use state data if available, otherwise use fetched data
  const displayPackage = state?.package || packageData;

  // Mutations
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Package> }) =>
      updatePackage(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('packages:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['package', id] });
      setIsEditModalOpen(false);
    },
    onError: () => {
      message.error(t('packages:updateError'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePackage(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('packages:deleteSuccess'));
      navigate('/residents/packages');
    },
    onError: () => {
      message.error(t('packages:deleteError'));
    },
  });

  // Loading state - only show if no state data is available
  if (isLoading && !state?.package) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayPackage) {
    return <Card>{t('packages:notFound')}</Card>;
  }

  const getStatusColor = (status: PackageStatus) => {
    const colors: Record<PackageStatus, string> = {
      pending: 'warning',
      received: 'processing',
      notified: 'cyan',
      collected: 'success',
      expired: 'error',
      returned: 'default',
    };
    return colors[status];
  };

  const getTypeColor = (type: PackageType) => {
    const colors: Record<PackageType, string> = {
      regular: 'blue',
      express: 'green',
      fragile: 'orange',
      oversized: 'purple',
      other: 'default',
    };
    return colors[type];
  };

  const getDeliveryServiceColor = (service: DeliveryService) => {
    const colors: Record<DeliveryService, string> = {
      fedex: 'purple',
      ups: 'brown',
      dhl: 'red',
      usps: 'blue',
      amazon: 'orange',
      other: 'default',
    };
    return colors[service];
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleEditSuccess = (data: Omit<Package, 'id'>) => {
    updateMutation.mutate({ id: displayPackage.id, data });
  };

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(displayPackage.id);
  };

  // Prepare tab items
  const tabItems = [
    {
      key: 'details',
      label: t('packages:details'),
      children: (
        <Card>
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('packages:trackingNumber')} span={2}>
              <Text strong>{displayPackage.trackingNumber}</Text>
            </Descriptions.Item>
            
            <Descriptions.Item label={t('packages:type')}>
              <Tag color={getTypeColor(displayPackage.type)}>
                {t(`packages:types.${displayPackage.type}`)}
              </Tag>
            </Descriptions.Item>
            
            <Descriptions.Item label={t('packages:status')}>
              <Tag color={getStatusColor(displayPackage.status)}>
                {t(`packages:statuses.${displayPackage.status}`)}
              </Tag>
            </Descriptions.Item>
            
            <Descriptions.Item label={t('packages:deliveryService')}>
              <Tag color={getDeliveryServiceColor(displayPackage.deliveryService)}>
                {t(`packages:deliveryServices.${displayPackage.deliveryService}`)}
              </Tag>
            </Descriptions.Item>
            
            <Descriptions.Item label={t('packages:receivedAt')}>
              {displayPackage.receivedAt ? dayjs(displayPackage.receivedAt).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label={t('packages:collectedAt')}>
              {displayPackage.collectedAt ? dayjs(displayPackage.collectedAt).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label={t('packages:expiryDate')}>
              {displayPackage.expiryDate ? dayjs(displayPackage.expiryDate).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label={t('packages:description')} span={2}>
              {displayPackage.description}
            </Descriptions.Item>
            
            {displayPackage.resident && (
              <Descriptions.Item label={t('packages:resident')}>
                {displayPackage.resident.name}
              </Descriptions.Item>
            )}
            
            {displayPackage.employee && (
              <Descriptions.Item label={t('packages:employee')}>
                {displayPackage.employee.name}
              </Descriptions.Item>
            )}
            
            {displayPackage.sender && (
              <>
                <Descriptions.Item label={t('packages:senderName')}>
                  {displayPackage.sender.name}
                </Descriptions.Item>
                <Descriptions.Item label={t('packages:senderPhone')}>
                  {displayPackage.sender.phone || '-'}
                </Descriptions.Item>
              </>
            )}
            
            {displayPackage.notes && (
              <Descriptions.Item label={t('packages:notes')} span={2}>
                {displayPackage.notes}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      ),
    },
  ];

  // Add resident tab if available
  if (displayPackage.resident) {
    tabItems.push({
      key: 'resident',
      label: t('packages:resident'),
      children: <ResidentTab residentId={displayPackage.resident.id} />,
    });
  }

  // Add employee tab if available
  if (displayPackage.employee) {
    tabItems.push({
      key: 'employee',
      label: t('packages:employee'),
      children: <EmployeeTab employeeId={displayPackage.employee.id} />,
    });
  }

  // Add activity log tab
  tabItems.push({
    key: 'activity',
    label: t('packages:activityLog'),
    children: (
      <Card>
        <ActivityLogList activityRecords={displayPackage.activityRecords || []} />
      </Card>
    ),
  });

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              {t('common:back')}
            </Button>
            <Title level={3}>
              {t('packages:package')} #{displayPackage.trackingNumber}
            </Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Tabs defaultActiveKey="details" items={tabItems} />

      {/* Modals */}
      <PackageModal
        open={isEditModalOpen}
        package={displayPackage}
        onSuccess={handleEditSuccess}
        onCancel={() => setIsEditModalOpen(false)}
      />

      <DeleteConfirmationModal
        visible={isDeleteModalOpen}
        title={t('packages:deleteConfirmTitle')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
};

export default PackageDetailsPage; 